"use client";

import { c as _c } from "react/compiler-runtime";
import type { ICustomImage } from '@filmweb/sanity-types';
import { useTrailerContext } from 'src/components/film/TrailerWrapper';
import MissingImage from "src/components/images/MissingImage";
import Picture, { PictureFallbackImage, PictureSource } from 'src/components/images/Picture';
import type { ImageVersion } from 'src/lib/movieinfo/movieinfotypes';
import { useState } from 'react';
import classNames from 'classnames';
import styles from './FilmTopImage.module.css';
const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND;

//#region [Props]
type FilmTopImageProps = {
  cmsImage?: ICustomImage | null;
  cmsPosterImage?: ICustomImage | null;
  streamingImages?: ImageVersion[];
  streamingPosterImages?: ImageVersion[];
  fallbackAltText: string;
  isStreamingContext: boolean;
  sanityPosterUrl?: string | null;
  sanityWideImageUrl?: string | null;
};
//#endregion

//#region [Component]
export default function FilmTopImage(t0) {
  const $ = _c(16);
  const {
    cmsImage,
    cmsPosterImage,
    streamingImages,
    streamingPosterImages,
    fallbackAltText,
    isStreamingContext,
    sanityPosterUrl,
    sanityWideImageUrl
  } = t0;
  const trailerContext = useTrailerContext();
  let t1;
  if ($[0] !== streamingPosterImages) {
    t1 = streamingPosterImages?.sort(_temp).find(_temp2)?.url;
    $[0] = streamingPosterImages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const streamingPosterImageFallback = t1;
  const _streamingImages = streamingImages?.sort(_temp3).filter(_temp4);
  const [showEntirePoster, setShowEntirePoster] = useState(false);
  const hasMobileImage = isStreamingContext && (!!(sanityPosterUrl || cmsPosterImage) || !!streamingPosterImageFallback) || !isStreamingContext && (!!cmsPosterImage || !!sanityPosterUrl);
  const hasDesktopImage = isStreamingContext && (!!(sanityWideImageUrl || cmsImage) || !!streamingImages) || !isStreamingContext && (!!cmsImage || !!sanityWideImageUrl);
  const t2 = hasDesktopImage && !hasMobileImage;
  let t3;
  if ($[2] !== showEntirePoster || $[3] !== t2 || $[4] !== trailerContext.isPlaying) {
    t3 = classNames(styles.topImageContainer, {
      [styles.isUsingWideFallback]: t2,
      [styles.isShowingTrailer]: trailerContext.isPlaying,
      [styles.showAll]: showEntirePoster
    });
    $[2] = showEntirePoster;
    $[3] = t2;
    $[4] = trailerContext.isPlaying;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== showEntirePoster) {
    t4 = () => setShowEntirePoster(!showEntirePoster);
    $[6] = showEntirePoster;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== hasDesktopImage || $[9] !== hasMobileImage) {
    t5 = !hasMobileImage && !hasDesktopImage && <MissingImage className={styles.missingMobileImage} aspect="112/156" missingText="Mangler plakat" />;
    $[8] = hasDesktopImage;
    $[9] = hasMobileImage;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = (hasMobileImage || hasDesktopImage) && <figure><Picture>{isStreamingContext && <>{hasDesktopImage && <>{(sanityWideImageUrl || cmsImage) && <><PictureSource media="(min-width: 641px)" imgUrl={sanityWideImageUrl} cmsImage={cmsImage} width={1010} aspect={2.269662921348315} hotspot={cmsImage?.asset?.hotspot} /><PictureSource media="(min-width: 550px)" imgUrl={sanityWideImageUrl} cmsImage={cmsImage} width={640} aspect={2.269662921348315} hotspot={cmsImage?.asset?.hotspot} /></>}{!(sanityWideImageUrl || cmsImage) && (_streamingImages?.length ?? 0) > 0 && <>{_streamingImages?.map((img_1, idx) => <PictureSource media={idx === _streamingImages.length - 1 ? "(min-width: 550px)" : `(min-width: ${img_1.width}px)`} key={idx} imgUrl={img_1.url} width={img_1.width} />)}</>}</>}{hasMobileImage && <>{(sanityPosterUrl || cmsPosterImage) && <PictureFallbackImage alt={cmsImage?.asset?.altText ?? fallbackAltText} imageUrl={sanityPosterUrl} cmsImage={cmsPosterImage} width={640} height={960} />}{!(sanityPosterUrl || cmsPosterImage) && streamingPosterImageFallback && <PictureFallbackImage alt={cmsImage?.asset?.altText ?? fallbackAltText} imageUrl={streamingPosterImageFallback} width={640} height={960} />}</>}{!hasMobileImage && <>{(sanityWideImageUrl || cmsImage) && <PictureFallbackImage alt={cmsImage?.asset?.altText ?? fallbackAltText} imageUrl={sanityWideImageUrl} cmsImage={cmsImage} hotspot={cmsImage?.asset?.hotspot} width={640} height={320} />}{!sanityWideImageUrl && (_streamingImages?.length ?? 0) > 0 && <PictureFallbackImage alt={cmsImage?.asset?.altText ?? fallbackAltText} imageUrl={_streamingImages[_streamingImages.length - 1].url} width={640} height={320} />}</>}</>}{!isStreamingContext && <>{hasDesktopImage && (cmsImage || sanityWideImageUrl) && <><PictureSource media="(min-width: 641px)" cmsImage={cmsImage} imgUrl={sanityWideImageUrl} width={1010} aspect={2.269662921348315} hotspot={cmsImage?.asset?.hotspot} /><PictureSource media="(min-width: 550px)" cmsImage={cmsImage} imgUrl={sanityWideImageUrl} width={640} aspect={2.269662921348315} hotspot={cmsImage?.asset?.hotspot} /></>}{hasMobileImage && (sanityPosterUrl || cmsPosterImage) && <PictureFallbackImage alt={cmsImage?.asset?.altText ?? fallbackAltText} imageUrl={sanityPosterUrl} cmsImage={cmsPosterImage} width={640} height={960} />}{!hasMobileImage && (sanityWideImageUrl || cmsImage) && <PictureFallbackImage alt={cmsImage?.asset?.altText ?? fallbackAltText} imageUrl={sanityWideImageUrl} cmsImage={cmsImage} hotspot={cmsImage?.asset?.hotspot} width={640} height={320} />}</>}</Picture>{BRAND === "kinodk" && cmsImage?.asset?.copyright?.trim() && <figcaption className={styles.SFigCaption}><small>{!cmsImage?.asset?.copyright.startsWith("\xA9") ? "\xA9" : " "}{cmsImage?.asset?.copyright}</small></figcaption>}</figure>;
  let t7;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <div className={t3} onClick={t4}>{t5}{t6}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
//#endregion
function _temp4(img_0) {
  return img_0.width > 640;
}
function _temp3(a_0, b_0) {
  return a_0.width > b_0.width ? -1 : 1;
}
function _temp2(img) {
  return img.width >= 640;
}
function _temp(a, b) {
  return a.width < b.width ? -1 : 1;
}